import _ from "lodash";
/** external */
import React, { useState } from "react";
import { connect } from "react-redux";
import {
  IconButton,
  Tooltip,
  Dropdown,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import PropTypes from "prop-types";
import classNames from "classnames";
/** internal */
import { getCustomFields } from "Worksheet/modules/WorksheetUtils";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import {
  FIELD_UID_ASSESSMENT_TOOL,
  RESOURCE_TYPE_QUESTION,
  FIELD_UID_ADD_LEARNING_GOALS,
} from "Worksheet/modules/WorksheetConstants";
import CustomDropdown from "UnitPlans/components/IBcomponents/CustomDropdown";
import ConditionalWrapper from "Utils/ConditionalWrapper";
/** relative */
import classes from "./AddCustomFields.scss";
import DynamicFieldsViewV2 from "../DynamicFieldsViewV2";

const AddCustomFields = props => {
  const {
    dynamicFields,
    t,
    onOptionClick,
    curriculumType,
    isSubQuestion,
    showAddQuestionDropdown,
    isGeneratingAIQuestions,
    assessmentTools,
    learningGoalsDropdownOptions,
    assessmentId,
    handleAssessmentToolDropdownClick,
    handleLearningGoalsDropdownClick,
    isFromInternalTool,
  } = props;
  const [showAssessmentToolsTooltip, setShowAssessmentToolsTooltip] = useState(
    !isGeneratingAIQuestions
  );

  const customFields = getCustomFields({
    fields: dynamicFields,
    t,
    isSubQuestion,
    curriculumType,
    isFromInternalTool,
  });

  const containerClasses = classNames(classes.container, {
    [classes.removeBorder]: showAddQuestionDropdown,
  });

  const handleDropdownClick = async ({ key, option }) => {
    const value = _.find(dynamicFields, { uid: key });

    if (_.isEmpty(value)) {
      await onOptionClick?.({ key });
    }
    if (_.isEqual(FIELD_UID_ADD_LEARNING_GOALS, key)) {
      await handleLearningGoalsDropdownClick?.(option);
    }
    if (_.isEqual(FIELD_UID_ASSESSMENT_TOOL, key)) {
      setShowAssessmentToolsTooltip(true);
      handleAssessmentToolDropdownClick?.(option);
    }
  };

  const getDropdownComponent = ({ key, options, icon, label }) => {
    switch (key) {
      case FIELD_UID_ASSESSMENT_TOOL: {
        let tooltipLabel;
        if (showAssessmentToolsTooltip) {
          const selectedAssessmentTools = _.map(
            _.get(assessmentTools, "edges", []),
            ({ node }) => _.get(node, "assessmentToolType")
          );
          const isDisabled = _.isEqual(
            _.size(selectedAssessmentTools),
            _.size(options)
          );

          tooltipLabel = isDisabled
            ? t("worksheet:label_with_already_added", {
                label,
              })
            : label;
        }

        return (
          <Tooltip
            tooltip={showAssessmentToolsTooltip ? tooltipLabel : null}
            placement={"bottom"}
            key={key}
            visible={showAssessmentToolsTooltip}
          >
            <div aria-label={tooltipLabel}>
              <CustomDropdown
                options={options}
                assessmentTools={_.get(assessmentTools, "edges", [])}
                curriculumProgramType={curriculumType}
                onChangeDropDownMenuOption={option =>
                  handleDropdownClick({ key, option })
                }
                isWorksheetDynamicField={true}
                parentType={RESOURCE_TYPE_QUESTION}
                assessmentId={assessmentId}
                isDropdownDisabled={isGeneratingAIQuestions}
                toggleTooltip={setShowAssessmentToolsTooltip}
                testId={`worksheet-addCustomFields-fieldButton-${_.camelCase(
                  label
                )}`}
              />
            </div>
          </Tooltip>
        );
      }
      case FIELD_UID_ADD_LEARNING_GOALS:
        return (
          <ConditionalWrapper
            condition={!isGeneratingAIQuestions}
            type={"wrapperAsElement"}
            wrapper={
              <Tooltip
                tooltip={t("common:pyp_elements")}
                placement={"bottom"}
                key={key}
              />
            }
          >
            <Dropdown
              overlay={
                <DropdownMenu
                  options={learningGoalsDropdownOptions}
                  onClick={option => handleDropdownClick({ key, option })}
                  testId={"assignmentCreation:addLearningGoals"}
                />
              }
              placement={"bottomRight"}
              disabled={isGeneratingAIQuestions}
            >
              <IconButton
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                size="large"
                _legacy_variant="outlined-subtle"
                icon={icon}
                _legacy_size="large"
                aria-label={label}
                testId={`worksheet-addCustomFields-fieldButton-${_.camelCase(
                  label
                )}`}
              />
            </Dropdown>
          </ConditionalWrapper>
        );
      default:
        return null;
    }
  };

  return (
    <div className={containerClasses}>
      <div className={classes.hide}>
        <DynamicFieldsViewV2
          {...props}
          isDynamicFieldDropdown={true}
          isSubQuestion={isSubQuestion}
        />
      </div>
      {_.map(customFields, field => {
        const { key, label, icon, isDisabled, isDropdown, options } = field;

        const tooltipLabel = isDisabled
          ? t("worksheet:label_with_already_added", { label })
          : label;

        return isDropdown ? (
          <div key={key}>
            {getDropdownComponent({ key, options, icon, label })}
          </div>
        ) : (
          <ConditionalWrapper
            condition={!isGeneratingAIQuestions}
            type={"wrapperAsElement"}
            wrapper={
              <Tooltip tooltip={tooltipLabel} placement={"bottom"} key={key} />
            }
          >
            <IconButton
              dsVersion="2.0"
              type="outlined"
              variant="neutral"
              size="large"
              _legacy_variant="outlined-subtle"
              onClick={() => onOptionClick({ key })}
              icon={icon}
              disabled={isDisabled || isGeneratingAIQuestions}
              _legacy_size="large"
              testId={`worksheet-addCustomFields-fieldButton-${_.camelCase(
                label
              )}`}
              aria-label={tooltipLabel}
            />
          </ConditionalWrapper>
        );
      })}
    </div>
  );
};

AddCustomFields.propTypes = {
  dynamicFields: PropTypes.array,
  onOptionClick: PropTypes.func,
  t: PropTypes.func,
  isSubQuestion: PropTypes.bool,
  showAddQuestionDropdown: PropTypes.bool,
  curriculumType: PropTypes.string,
  isGeneratingAIQuestions: PropTypes.bool,
  questionId: PropTypes.string,
  handleAssessmentToolDropdownClick: PropTypes.func,
  questionFields: PropTypes.arrayOf(PropTypes.object),
};

AddCustomFields.defaultProps = {
  dynamicFields: [],
  onOptionClick: _.noop,
  t: _.noop,
  isSubQuestion: false,
  showAddQuestionDropdown: false,
  handleAssessmentToolDropdownClick: _.noop,
  questionFields: [],
};

const mapStateToProps = state => {
  const curriculumType = getCurrentCurriculumProgramType(state);
  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );
  return {
    curriculumType,
    isGeneratingAIQuestions,
  };
};

export default connect(mapStateToProps, null)(AddCustomFields);
